import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Auth from '../../../../lib/commons/auth'
import {
  fetchTenantDetail,
  TenantDetailDto,
  updateTenantDelta,
} from '../../../../infrastructure/api/tenants'
import { fromDetail, toDeltaInput } from '../model'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { extractValuesFromResponse } from '../../../../lib/commons/api'
import { handleWarning } from '../../../../handlers/globalErrorHandler'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../..'
import { useTenantFormState } from './useTenantFormState'

// テナント設定画面の data フック。更新のみ（作成・削除は無い）。
// NOTE: APPLICATION_FUNCTION_EXTERNAL_ID はモジュールトップレベルで読むと
// pages/index.tsx との循環参照で初期化前アクセス（undefined）になるため、
// 必ず実行時（コールバック内）で参照する。
export const useTenantEditData = () => {
  const dispatch = useDispatch()
  // テナント認証済みでのみ到達する画面のため、現在テナントは必ず存在する。
  const tenantUuid = Auth.getCurrentTenant()!.tenantUuid
  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // isSubmitting は再描画まで反映されないため、その間に連続で保存されると同じ delta が
  // 二重に送信されうる。
  const isSubmittingRef = useRef(false)
  // isLoading は保存中に挟む再取得が終わった時点で false に戻るため、保存の完了より
  // 先にボタンが有効化されてしまう。押しても isSubmittingRef に弾かれて無反応になる
  // ため、保存の間ずっと無効化できるよう送信中かどうかを別に持つ。
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entity, setEntity] = useState<TenantDetailDto | undefined>()
  const {
    formModel,
    setFormModel,
    formModelRef,
    onChangeTenant,
    edited,
    refreshedPageData,
    editedFormData,
    refreshEditedFormData,
  } = useTenantFormState()

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    try {
      const detail = await fetchTenantDetail(tenantUuid)
      if (!detail) return undefined
      setEntity(detail)
      setFormModel(fromDetail(detail))
      // 取得に失敗した場合に空のフォームモデルを実データとして描画したり、
      // 画面に残っている編集内容を保存できなくしたりしないよう、画面の表示と
      // 編集フラグのリセットは取得に成功した場合のみ行う。
      setIsInitialized(true)
      refreshedPageData()
      refreshEditedFormData()
      // dailyWorkHours / monthlyWorkDays は Auth が保持するテナント情報から
      // 工数換算の基準値として全画面が参照するため、取得のたびに更新しないと
      // 保存後もプロジェクト計画やレポートの数値が旧基準のまま残る。
      // 画面の表示には使わないため待たない。失敗はグローバルエラーハンドラで通知する。
      Auth.getCurrentTenant()?.refreshOrganization()
      return detail
    } finally {
      setIsLoading(false)
    }
  }, [tenantUuid, setFormModel, refreshedPageData, refreshEditedFormData])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const reload = useCallback(async () => {
    await fetchData()
  }, [fetchData])

  const submit = useCallback(async () => {
    if (isSubmittingRef.current) return
    const currentFormModel = formModelRef.current
    if (!entity) return
    isSubmittingRef.current = true
    setIsSubmitting(true)
    setIsLoading(true)
    try {
      const response = await updateTenantDelta(
        toDeltaInput(entity, currentFormModel)
      )
      if (!response) return
      // 更新がサーバー側で拒否されると 200 + ワーニングメッセージが返る
      // （更新は反映されない）ため、成功メッセージは出さずに警告を表示する。
      if (response.hasWarning) {
        const messages = extractValuesFromResponse(response.json, 'messages')
        handleWarning(messages, _ => ({
          code: currentFormModel.alias,
          name: currentFormModel.displayName,
        }))
        return
      }
      if (response.hasError) {
        dispatch(
          addScreenMessage(APPLICATION_FUNCTION_EXTERNAL_ID.TENANT_EDIT, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'tenantV2.update.failed' }),
          })
        )
        return
      }
      dispatch(
        addScreenMessage(APPLICATION_FUNCTION_EXTERNAL_ID.TENANT_EDIT, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'update.completed.title' }),
        })
      )
      // 更新自体は成功しているため、再取得が失敗しても編集マーカーは消す
      // （未保存の変更が残っているとユーザーに誤認させないため）。再取得の失敗の
      // 通知は、接続失敗ならfetchラッパー、それ以外はグローバルエラーハンドラに任せる。
      let refetched: TenantDetailDto | undefined
      try {
        refetched = await fetchData()
      } finally {
        if (!refetched) {
          refreshedPageData()
          refreshEditedFormData()
        }
      }
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
      setIsLoading(false)
    }
  }, [
    entity,
    fetchData,
    dispatch,
    formModelRef,
    refreshedPageData,
    refreshEditedFormData,
  ])

  return {
    isInitialized,
    isLoading,
    isSubmitting,
    edited,
    editedFormData,
    formModel,
    entity,
    onChangeTenant,
    submit,
    reload,
  }
}
